/**
 * Authentication service
 */
import api from './api';
import { User } from '../types/user';

export interface LoginCredentials {
  username: string;
  password: string;
}

export interface LoginResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  user: User;
  must_change_password?: boolean;
}

export const authService = {
  /**
   * Login via Microsoft SSO
   */
  async ssoLogin(idToken: string): Promise<LoginResponse> {
    const response = await api.post('/auth/sso/callback', { id_token: idToken });

    localStorage.setItem('access_token', response.data.access_token);
    localStorage.setItem('refresh_token', response.data.refresh_token);
    localStorage.setItem('user', JSON.stringify(response.data.user));
    localStorage.setItem('lastActivity', Date.now().toString());

    if (response.data.must_change_password) {
      localStorage.setItem('must_change_password', 'true');
    } else {
      localStorage.removeItem('must_change_password');
    }

    return response.data;
  },

  /**
   * Break-glass login - admin only, hidden page
   */
  async emergencyLogin(credentials: LoginCredentials): Promise<LoginResponse> {
    const formData = new URLSearchParams();
    formData.append('username', credentials.username);
    formData.append('password', credentials.password);

    const response = await api.post('/auth/emergency-login', formData.toString(), {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });

    localStorage.setItem('access_token', response.data.access_token);
    localStorage.setItem('refresh_token', response.data.refresh_token);
    localStorage.setItem('user', JSON.stringify(response.data.user));
    localStorage.setItem('lastActivity', Date.now().toString());

    return response.data;
  },

  /**
   * Logout user
   */
  async logout(): Promise<void> {
    try {
      // SECURITY: Backend clears httpOnly cookies (XSS protection)
      await api.post('/auth/logout');
    } finally {
      // Clear all auth data from local storage
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
      localStorage.removeItem('must_change_password');
      localStorage.removeItem('lastActivity'); // Clear idle timeout tracker
    }
  },

  /**
   * Get current user info
   */
  async getCurrentUser(): Promise<User> {
    const response = await api.get('/auth/me');
    return response.data;
  },

  /**
   * Get stored user from localStorage
   */
  getStoredUser(): User | null {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        return JSON.parse(userStr);
      } catch {
        return null;
      }
    }
    return null;
  },

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return !!localStorage.getItem('access_token');
  },
};
